import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import {
  getPracticeSetQuestions,
  submitPracticeAttempt,
  countUnseenSets,
  generatePracticeSet,
  type PracticeTestQuestion,
  type QuestionAnswer,
  type QuestionType,
  type PracticeSegment,
  type CompletedSegment,
} from '../services/practiceTestService';
import { getExamBlueprint, getRegion, type PaperBlueprint } from '../config/regionConfig';
import { buildProvenanceLabel } from '../utils/provenanceLabel';
import QuestionRenderer from '../components/QuestionRenderer';
import QuestionPalette from '../components/QuestionPalette';
import { Clock, ChevronLeft, ChevronRight, Grid3X3, Bookmark, Send, AlertTriangle, BookOpen, Coffee } from 'lucide-react';

const AUTOSAVE_KEY = (setId: string) => `rewise_practice_${setId}`;

type LoadedQuestion = PracticeTestQuestion & {
  __setId?: number;
  __subject?: string;
  __origSectionLabel?: string | null;
};

type LocState = {
  showTimer?: boolean;
  examType?: string;
  subject?: string;
  blueprint?: PaperBlueprint;
  isRetake?: boolean;
  // Continuous-flow (SAT, JEE Main/Advanced, NEET) fields:
  segments?: PracticeSegment[];
  segmentIndex?: number;
  continuousMode?: 'segmented' | 'pooled';
  breakMinutes?: number;
  completedSegments?: CompletedSegment[];
  /** Shared across every subject's attempt in one continuous-flow sitting — groups them for the full-exam-simulation cap. */
  sessionId?: string;
};

/** Outer shell: remounts the inner component whenever the active setId changes so that
 *  per-segment local state (answers, timer, index) starts fresh for each subject. */
export default function PracticeTestTaking() {
  const { setId } = useParams<{ setId: string }>();
  return <PracticeTestTakingSession key={setId} />;
}

function PracticeTestTakingSession() {
  const { setId } = useParams<{ setId: string }>();
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const locState = location.state as LocState | null;
  const showTimer = locState?.showTimer ?? true;

  const segments = locState?.segments;
  const segmentIndex = locState?.segmentIndex ?? 0;
  const continuousMode = locState?.continuousMode;
  const breakMinutes = locState?.breakMinutes ?? 0;
  const activeSegment = segments?.[segmentIndex];

  const pooledBlueprint: PaperBlueprint | null = continuousMode === 'pooled' && segments
    ? {
        durationMinutes: segments.reduce((s, seg) => s + seg.blueprint.durationMinutes, 0),
        totalMarks: segments.reduce((s, seg) => s + seg.blueprint.totalMarks, 0),
        sections: segments.flatMap(seg => seg.blueprint.sections.map(sec => ({ ...sec, label: `${seg.subject} — ${sec.label}` }))),
      }
    : null;

  const blueprint = pooledBlueprint ?? locState?.blueprint ?? activeSegment?.blueprint ?? null;
  const durationMinutes = blueprint?.durationMinutes ?? null;
  const readingTimeMinutes = blueprint?.readingTimeMinutes ?? 0;

  const [questions, setQuestions] = useState<LoadedQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answersMap, setAnswersMap] = useState<Map<number, QuestionAnswer>>(new Map());
  const [markedForReview, setMarkedForReview] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [attemptLimitWarning, setAttemptLimitWarning] = useState<string | null>(null);
  // reading phase comes first if the blueprint has readingTimeMinutes
  const [phase, setPhase] = useState<'reading' | 'exam'>(readingTimeMinutes > 0 ? 'reading' : 'exam');
  const pageLoadedAt = useRef(Date.now());
  const [testStartedAt, setTestStartedAt] = useState(() => readingTimeMinutes > 0 ? 0 : Date.now());
  const questionStartRef = useRef(Date.now());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Break between subjects (segmented continuous-flow exams, e.g. SAT R&W -> break -> Math)
  const [pendingAdvance, setPendingAdvance] = useState<{ completed: CompletedSegment[]; nextIndex: number } | null>(null);
  const breakStartRef = useRef<number | null>(null);
  const [breakElapsed, setBreakElapsed] = useState(0);

  // Timer tick — tracks reading countdown or exam elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      if (phase === 'reading') {
        setElapsedSeconds(Math.floor((now - pageLoadedAt.current) / 1000));
      } else if (testStartedAt > 0) {
        setElapsedSeconds(Math.floor((now - testStartedAt) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, testStartedAt]);

  // Auto-transition from reading to exam when reading time expires
  useEffect(() => {
    if (phase === 'reading' && elapsedSeconds >= readingTimeMinutes * 60) {
      setPhase('exam');
      setTestStartedAt(Date.now());
    }
  }, [phase, elapsedSeconds, readingTimeMinutes]);

  function advanceToNextSegment(adv: { completed: CompletedSegment[]; nextIndex: number }) {
    if (!segments) return;
    const next = segments[adv.nextIndex];
    navigate(`/practice-test/attempt/${next.setId}`, {
      replace: true,
      state: {
        ...locState,
        segmentIndex: adv.nextIndex,
        completedSegments: adv.completed,
      } as LocState,
    });
  }

  // Break countdown between segments
  useEffect(() => {
    if (!pendingAdvance) return;
    if (breakMinutes <= 0) {
      advanceToNextSegment(pendingAdvance);
      return;
    }
    breakStartRef.current = Date.now();
    setBreakElapsed(0);
    const interval = setInterval(() => {
      setBreakElapsed(Math.floor((Date.now() - (breakStartRef.current ?? Date.now())) / 1000));
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAdvance]);

  useEffect(() => {
    if (pendingAdvance && breakMinutes > 0 && breakElapsed >= breakMinutes * 60) {
      advanceToNextSegment(pendingAdvance);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breakElapsed]);

  // Load questions + restore autosave
  useEffect(() => {
    async function load() {
      if (!setId) return;

      if (continuousMode === 'pooled' && segments) {
        const all: LoadedQuestion[] = [];
        for (const seg of segments) {
          const qs = await getPracticeSetQuestions(seg.setId);
          for (const q of qs) {
            all.push({
              ...q,
              __setId: seg.setId,
              __subject: seg.subject,
              __origSectionLabel: q.section_label,
              section_label: q.section_label ? `${seg.subject} — ${q.section_label}` : seg.subject,
            });
          }
        }
        setQuestions(all);
      } else {
        const data = await getPracticeSetQuestions(Number(setId));
        setQuestions(data);
      }

      // Restore autosave
      try {
        const saved = localStorage.getItem(AUTOSAVE_KEY(setId));
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed.answersMap) {
            const restored = new Map<number, QuestionAnswer>(Object.entries(parsed.answersMap).map(([k, v]) => [Number(k), v as QuestionAnswer]));
            setAnswersMap(restored);
          }
          if (parsed.markedForReview) {
            setMarkedForReview(new Set(parsed.markedForReview));
          }
          if (typeof parsed.currentIndex === 'number') {
            setCurrentIndex(parsed.currentIndex);
          }
        }
      } catch { /* ignore */ }

      setLoading(false);
      questionStartRef.current = Date.now();
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setId]);

  // Autosave every 30s
  useEffect(() => {
    if (!setId || questions.length === 0) return;
    const interval = setInterval(() => {
      const obj = {
        answersMap: Object.fromEntries(answersMap),
        markedForReview: [...markedForReview],
        currentIndex,
      };
      localStorage.setItem(AUTOSAVE_KEY(setId), JSON.stringify(obj));
    }, 30000);
    return () => clearInterval(interval);
  }, [setId, answersMap, markedForReview, currentIndex, questions.length]);

  // Grade a question answer
  const gradeAnswer = useCallback((q: PracticeTestQuestion, ans: Partial<QuestionAnswer>): QuestionAnswer => {
    const type: QuestionType = q.question_type || 'mcq_single';
    const base: QuestionAnswer = {
      question_id: q.question_id,
      question_type: type,
      selected: ans.selected,
      selected_options: ans.selected_options,
      numerical_answer: ans.numerical_answer,
      text_answer: ans.text_answer,
      is_correct: null,
      marks_awarded: 0,
      time_spent_ms: ans.time_spent_ms || 0,
    };

    if (type === 'mcq_single' || type === 'assertion_reasoning') {
      if (ans.selected != null && q.correct_option_index != null) {
        const correct = ans.selected === q.correct_option_index;
        base.is_correct = correct;
        base.marks_awarded = correct ? q.marks : -(q.negative_marks || 0);
      }
    } else if (type === 'mcq_multi') {
      if (ans.selected_options && ans.selected_options.length > 0 && q.correct_options) {
        const correctSet = new Set(q.correct_options);
        const studentSet = new Set(ans.selected_options);
        const correct = correctSet.size === studentSet.size && [...correctSet].every(c => studentSet.has(c));
        base.is_correct = correct;
        base.marks_awarded = correct ? q.marks : -(q.negative_marks || 0);
      }
    } else if (type === 'numerical') {
      if (ans.numerical_answer != null && q.correct_answer_numerical != null) {
        const tolerance = q.numerical_tolerance || 0;
        const correct = Math.abs(ans.numerical_answer - q.correct_answer_numerical) <= tolerance;
        base.is_correct = correct;
        base.marks_awarded = correct ? q.marks : 0;
      }
    }
    // short_answer / long_answer: is_correct stays null (not auto-graded)

    return base;
  }, []);

  // ── Attempt-limit helpers (JEE Section B "attempt 5 of 10", NEET "attempt 10 of 15") ──
  function getSectionAttemptLimit(sectionLabel: string): number | undefined {
    return blueprint?.sections.find(s => s.label === sectionLabel)?.attemptLimit;
  }

  function getSectionAttemptCount(sectionLabel: string): number {
    return questions.filter(q => q.section_label === sectionLabel && answersMap.has(q.question_id)).length;
  }

  if (isLoading || !session || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-warm" />
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <main className="max-w-2xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">No questions found for this test set.</p>
      </main>
    );
  }

  const current = questions[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === questions.length - 1;
  const currentAnswer = answersMap.get(current.question_id);

  const answeredCount = answersMap.size;
  const reviewCount = markedForReview.size;
  const unansweredCount = questions.length - answeredCount;

  function formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  const readingTimeRemaining = phase === 'reading' ? (readingTimeMinutes * 60) - elapsedSeconds : null;
  const timeRemaining = phase === 'exam' && durationMinutes ? (durationMinutes * 60) - elapsedSeconds : null;
  const timeDisplay = timeRemaining != null
    ? formatTime(Math.max(0, timeRemaining))
    : formatTime(elapsedSeconds);
  const isTimeWarning = timeRemaining != null && timeRemaining <= 300 && timeRemaining > 0;
  const isTimeUp = timeRemaining != null && timeRemaining <= 0;

  function handleAnswerUpdate(update: Partial<QuestionAnswer>) {
    const sectionLabel = current.section_label;
    const alreadyAnswered = answersMap.has(current.question_id);

    // Enforce attempt-N-of-M for sections with an attemptLimit (e.g. JEE Section B)
    if (sectionLabel && !alreadyAnswered) {
      const limit = getSectionAttemptLimit(sectionLabel);
      if (limit !== undefined) {
        const count = getSectionAttemptCount(sectionLabel);
        if (count >= limit) {
          setAttemptLimitWarning(`You can only attempt ${limit} questions in this section. Clear another answer first.`);
          return;
        }
      }
    }
    setAttemptLimitWarning(null);

    const timeSpent = Date.now() - questionStartRef.current;
    const existing = answersMap.get(current.question_id);
    const merged = { ...existing, ...update, time_spent_ms: (existing?.time_spent_ms || 0) + timeSpent };
    const graded = gradeAnswer(current, merged);
    graded.time_spent_ms = merged.time_spent_ms;

    setAnswersMap(prev => {
      const next = new Map(prev);
      next.set(current.question_id, graded);
      return next;
    });
  }

  function handleToggleReview() {
    setMarkedForReview(prev => {
      const next = new Set(prev);
      if (next.has(current.question_id)) {
        next.delete(current.question_id);
      } else {
        next.add(current.question_id);
      }
      return next;
    });
  }

  function navigateTo(idx: number) {
    questionStartRef.current = Date.now();
    setCurrentIndex(idx);
    setShowPalette(false);
  }

  function handlePrev() {
    if (!isFirst) navigateTo(currentIndex - 1);
  }

  function handleNext() {
    if (!isLast) navigateTo(currentIndex + 1);
  }

  async function handleSubmit() {
    if (!session || !setId) return;
    setSubmitting(true);
    setShowSubmitConfirm(false);

    // Clear autosave
    localStorage.removeItem(AUTOSAVE_KEY(setId));

    const finalAnswers = [...answersMap.values()];
    const examStart = testStartedAt > 0 ? testStartedAt : Date.now();
    const timeTaken = Math.floor((Date.now() - examStart) / 1000);
    const isRetake = locState?.isRetake ?? false;
    const region = getRegion();

    // ── Pooled (JEE Main/Advanced, NEET): one merged paper, submit one attempt per subject ──
    if (continuousMode === 'pooled' && segments) {
      const completed: CompletedSegment[] = [];
      let lastAttemptId: number | null = null;

      for (const seg of segments) {
        const segQuestions = questions.filter(q => q.__setId === seg.setId);
        const segAnswers = segQuestions
          .map(q => answersMap.get(q.question_id))
          .filter((a): a is QuestionAnswer => !!a);
        const score = segAnswers.filter(a => a.is_correct === true).length;
        const marksObtained = segAnswers.reduce((sum, a) => sum + a.marks_awarded, 0);
        const maxMarks = segQuestions.reduce((sum, q) => sum + q.marks, 0);

        const sectionScores: Record<string, { obtained: number; max: number }> = {};
        for (const q of segQuestions) {
          const label = q.__origSectionLabel || q.section_label || 'default';
          if (!sectionScores[label]) sectionScores[label] = { obtained: 0, max: 0 };
          sectionScores[label].max += q.marks;
          const ans = answersMap.get(q.question_id);
          if (ans) sectionScores[label].obtained += ans.marks_awarded;
        }

        const attemptId = await submitPracticeAttempt(
          session.student_id, seg.setId, score, segQuestions.length, segAnswers, timeTaken,
          marksObtained, maxMarks, sectionScores, isRetake, locState?.sessionId
        );
        lastAttemptId = attemptId;
        completed.push({ subject: seg.subject, attemptId, score, total: segQuestions.length, marksObtained, maxMarks });

        if (!isRetake && locState?.examType) {
          const examType = locState.examType;
          countUnseenSets(session.student_id, examType, session.grade_level, region, seg.subject).then(unseen => {
            if (unseen === 0) generatePracticeSet(examType, session.grade_level, seg.subject, session.student_id, region, seg.blueprint).catch(() => { /* silent */ });
          });
        }
      }

      const totalMarksObtained = completed.reduce((s, c) => s + c.marksObtained, 0);
      const totalMaxMarks = completed.reduce((s, c) => s + c.maxMarks, 0);
      const totalScore = completed.reduce((s, c) => s + c.score, 0);
      const totalQuestions = completed.reduce((s, c) => s + c.total, 0);

      navigate(`/practice-test/results/${lastAttemptId || 0}`, {
        state: {
          score: totalScore,
          total: totalQuestions,
          timeTaken,
          answers: finalAnswers,
          questions,
          marksObtained: totalMarksObtained,
          maxMarks: totalMaxMarks,
          blueprint,
          examType: locState?.examType,
          isRetake,
          sessionResults: completed,
        },
      });
      return;
    }

    // ── Single-subject exam OR one segment of a segmented exam (SAT) ──
    const score = finalAnswers.filter(a => a.is_correct === true).length;
    const marksObtained = finalAnswers.reduce((sum, a) => sum + a.marks_awarded, 0);
    const maxMarks = questions.reduce((sum, q) => sum + q.marks, 0);

    const sectionScores: Record<string, { obtained: number; max: number }> = {};
    for (const q of questions) {
      const label = q.section_label || 'default';
      if (!sectionScores[label]) sectionScores[label] = { obtained: 0, max: 0 };
      sectionScores[label].max += q.marks;
      const ans = answersMap.get(q.question_id);
      if (ans) sectionScores[label].obtained += ans.marks_awarded;
    }

    const attemptId = await submitPracticeAttempt(
      session.student_id,
      Number(setId),
      score,
      questions.length,
      finalAnswers,
      timeTaken,
      marksObtained,
      maxMarks,
      sectionScores,
      isRetake,
      locState?.sessionId
    );

    const subjectForRegen = activeSegment?.subject ?? locState?.subject;

    // Background pre-generation: if pool is now empty for this student, generate next set silently.
    if (!isRetake && locState?.examType) {
      const examType = locState.examType;
      const bp = activeSegment?.blueprint ?? locState.blueprint ?? getExamBlueprint(examType) ?? null;
      countUnseenSets(session.student_id, examType, session.grade_level, region, subjectForRegen).then(
        (unseen) => {
          if (unseen === 0) {
            generatePracticeSet(examType, session.grade_level, subjectForRegen || examType, session.student_id, region, bp || undefined)
              .catch(() => { /* silent */ });
          }
        }
      );
    }

    const updatedCompleted: CompletedSegment[] = [
      ...(locState?.completedSegments ?? []),
      { subject: subjectForRegen ?? '', attemptId, score, total: questions.length, marksObtained, maxMarks },
    ];

    const isSegmentedNotLast = continuousMode === 'segmented' && segments && segmentIndex < segments.length - 1;

    if (isSegmentedNotLast) {
      // Don't show results yet — move straight into the mandatory break, then the next subject.
      setPendingAdvance({ completed: updatedCompleted, nextIndex: segmentIndex + 1 });
      setSubmitting(false);
      return;
    }

    navigate(`/practice-test/results/${attemptId || 0}`, {
      state: {
        score,
        total: questions.length,
        timeTaken,
        answers: finalAnswers,
        questions,
        marksObtained,
        maxMarks,
        sectionScores,
        blueprint,
        examType: locState?.examType,
        subject: subjectForRegen,
        isRetake,
        sessionResults: continuousMode === 'segmented' ? updatedCompleted : undefined,
      },
    });
  }

  const isReviewed = markedForReview.has(current.question_id);

  return (
    <main className="max-w-3xl mx-auto w-full px-4 sm:px-6 py-4 sm:py-6">

      {/* Reading time overlay — shown before exam starts */}
      {phase === 'reading' && !loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ocean-deep/90 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center animate-fade-in-up">
            <BookOpen className="w-12 h-12 text-coral-warm mx-auto mb-4" />
            <h3 className="font-bold text-ocean-deep text-xl mb-2">Reading Time</h3>
            <p className="text-gray-600 text-sm mb-6">
              Read through the paper carefully. You <strong>cannot</strong> write answers during this time.
            </p>
            <div className="text-5xl font-mono font-bold text-coral-warm mb-2">
              {formatTime(Math.max(0, readingTimeRemaining ?? 0))}
            </div>
            <p className="text-xs text-gray-400 mb-6">Writing begins automatically when reading time ends</p>
            <button
              onClick={() => { setPhase('exam'); setTestStartedAt(Date.now()); }}
              className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors"
            >
              Skip reading time
            </button>
          </div>
        </div>
      )}

      {/* Break overlay — mandatory break between subjects (SAT: R&W -> break -> Math) */}
      {pendingAdvance && breakMinutes > 0 && segments && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-amber-900/90 px-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl text-center animate-fade-in-up">
            <Coffee className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h3 className="font-bold text-ocean-deep text-xl mb-2">Break Time</h3>
            <p className="text-gray-600 text-sm mb-6">
              {segments[segmentIndex].subject} is complete. <strong>{segments[pendingAdvance.nextIndex].subject}</strong> begins automatically after your break.
            </p>
            <div className="text-5xl font-mono font-bold text-amber-600 mb-2">
              {formatTime(Math.max(0, breakMinutes * 60 - breakElapsed))}
            </div>
            <button
              onClick={() => advanceToNextSegment(pendingAdvance)}
              className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors"
            >
              Skip break
            </button>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div className="flex items-center justify-between mb-4 animate-fade-in-up">
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-ocean-deep">
            Q {currentIndex + 1}/{questions.length}
          </span>
          {continuousMode === 'segmented' && segments && (
            <span className="px-2 py-0.5 text-[10px] font-bold bg-orange-100 text-coral-warm rounded-full">
              {activeSegment?.subject} · {segmentIndex + 1}/{segments.length}
            </span>
          )}
          <button
            onClick={() => setShowPalette(!showPalette)}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <Grid3X3 className="w-3.5 h-3.5" />
            Palette
          </button>
        </div>

        <div className="flex items-center gap-3">
          {/* Timer */}
          {showTimer && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-mono font-bold ${
              isTimeUp ? 'bg-red-100 text-red-700 animate-pulse' :
              isTimeWarning ? 'bg-amber-100 text-amber-700' :
              'bg-gray-100 text-gray-600'
            }`}>
              <Clock className="w-4 h-4" />
              {timeDisplay}
              {timeRemaining != null && <span className="text-[10px] font-normal ml-1">left</span>}
            </div>
          )}

          {/* Submit button in header */}
          <button
            onClick={() => setShowSubmitConfirm(true)}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors shadow-sm"
          >
            <Send className="w-3.5 h-3.5" />
            Submit
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4">
        <div
          className="bg-gradient-to-r from-indigo-500 to-purple-600 h-1.5 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
        />
      </div>

      {/* Palette overlay */}
      {showPalette && (
        <div className="mb-4 animate-fade-in-up">
          <QuestionPalette
            questions={questions}
            answersMap={answersMap}
            markedForReview={markedForReview}
            currentIndex={currentIndex}
            onJumpTo={navigateTo}
            onClose={() => setShowPalette(false)}
          />
        </div>
      )}

      {/* Attempt-limit warning */}
      {attemptLimitWarning && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 animate-fade-in-up">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
          <p className="text-sm text-amber-700">{attemptLimitWarning}</p>
        </div>
      )}

      {/* Section attempt progress (for sections with attempt limit) */}
      {current.section_label && getSectionAttemptLimit(current.section_label) !== undefined && (
        <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl text-xs text-blue-700 animate-fade-in-up">
          {current.section_label}: {getSectionAttemptCount(current.section_label)} / {getSectionAttemptLimit(current.section_label)} answered
          <span className="text-blue-500 ml-1">(attempt limit)</span>
        </div>
      )}

      {/* Question card */}
      <div className="glass-card rounded-xl p-5 sm:p-7 shadow-lg animate-fade-in-up" key={current.question_id}>
        <div className="mb-2 text-[11px] font-medium text-gray-400">
          {buildProvenanceLabel({
            provenance: current.provenance,
            exam_type: locState?.examType,
            state_board: session?.state_board,
            year_level: session?.grade_level,
            source_year: current.source_year,
            source_session: current.source_session,
            source_question_number: current.source_question_number,
            marks: current.marks,
          })}
        </div>
        <QuestionRenderer
          question={current}
          answer={currentAnswer || {}}
          onAnswer={handleAnswerUpdate}
          questionNumber={currentIndex + 1}
        />
      </div>

      {/* Bottom nav */}
      <div className="mt-5 flex items-center justify-between gap-3 animate-fade-in-up">
        {/* Prev */}
        <button
          onClick={handlePrev}
          disabled={isFirst}
          className="flex items-center gap-1 px-4 py-3 text-sm font-medium text-gray-700 bg-white border-2 border-gray-200 hover:border-gray-300 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
          Prev
        </button>

        {/* Mark for review */}
        <button
          onClick={handleToggleReview}
          className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium rounded-xl transition-all border-2 ${
            isReviewed
              ? 'border-amber-400 bg-amber-50 text-amber-700'
              : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300'
          }`}
        >
          <Bookmark className={`w-4 h-4 ${isReviewed ? 'fill-amber-400' : ''}`} />
          {isReviewed ? 'Marked' : 'Review'}
        </button>

        {/* Next */}
        <button
          onClick={handleNext}
          disabled={isLast}
          className="flex items-center gap-1 px-4 py-3 text-sm font-medium text-white bg-coral-warm hover:bg-orange-600 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-md"
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Submit confirmation modal */}
      {showSubmitConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-fade-in-up">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <h3 className="font-bold text-ocean-deep">
                {continuousMode === 'segmented' && segments && segmentIndex < segments.length - 1
                  ? `Submit ${activeSegment?.subject}?`
                  : 'Submit Test?'}
              </h3>
            </div>

            <div className="space-y-2 mb-6 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Answered</span>
                <span className="font-bold text-green-600">{answeredCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Not Answered</span>
                <span className="font-bold text-gray-500">{unansweredCount}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Marked for Review</span>
                <span className="font-bold text-amber-500">{reviewCount}</span>
              </div>
            </div>

            {unansweredCount > 0 && (
              <p className="text-xs text-amber-600 mb-4">
                You have {unansweredCount} unanswered question{unansweredCount > 1 ? 's' : ''}. Are you sure you want to submit?
              </p>
            )}

            <p className="text-xs text-gray-500 mb-4">
              You'll see step-by-step explanations for every question once you submit.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowSubmitConfirm(false)}
                className="flex-1 py-3 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Go Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-3 text-sm font-bold text-white bg-green-600 hover:bg-green-700 rounded-xl transition-colors shadow-md disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Confirm Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Time up auto-prompt */}
      {isTimeUp && !showSubmitConfirm && !submitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl animate-fade-in-up text-center">
            <Clock className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <h3 className="font-bold text-ocean-deep text-lg mb-2">Time's Up!</h3>
            <p className="text-sm text-gray-600 mb-6">Your time for this paper has ended. Submit your answers now.</p>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full py-3 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors shadow-md disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit Now'}
            </button>
          </div>
        </div>
      )}
    </main>
  );
}
